import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { connectState } from '@examdojo/angular/util';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CHALLENGE_ACTIVITY_QUESTION_ATTEMPT_STATUSES,
  ChallengeActivityQuestionAttemptQuery,
  ChallengeActivityQuestionAttemptService,
  ChallengeActivityQuestionAttemptStatus,
  ChallengeActivityQuestionAttemptStoreModel,
} from '@examdojo/challenge';
import { ButtonComponent } from '@examdojo/core/button';
import { map } from 'rxjs';
import { IconComponent } from '@examdojo/core/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { ChallengeActivityQuestionStatusComponent } from './challenge-activity-question-status.component';

@Component({
  selector: 'dojo-challenge-activity-question-status-selector',
  standalone: true,
  imports: [ButtonComponent, IconComponent, MatMenuModule, ChallengeActivityQuestionStatusComponent, TranslocoPipe],
  template: `
    <div class="split-button flex w-fit">
      <dojo-button [fill]="'outline'" (click)="cycleStatus(state.currentStatus)" [size]="'s'" class="split-button-left">
        <dojo-challenge-activity-question-status [status]="state.currentStatus" />
        <span class="ml-2">{{ statusToLabelKey[state.currentStatus] | transloco }}</span>
      </dojo-button>

      <dojo-button [matMenuTriggerFor]="statusMenu" [fill]="'outline'" [size]="'s'" class="split-button-right">
        <dojo-icon [icon]="isStatusSelectorOpened() ? 'chevron-up' : 'chevron-down'" />
      </dojo-button>
    </div>

    <mat-menu #statusMenu="matMenu" class="custom-mat-menu w-[170px]">
      <div>
        @for (option of statusOptions; track $index) {
          <button
            mat-menu-item
            (click)="updateChallengeActivityQuestionAttemptStatus(option)"
            [class.active]="option === state.currentStatus"
          >
            <dojo-challenge-activity-question-status [status]="option" />

            <div class="ml-2 flex w-full items-center justify-between">
              <span class="text-sm">{{ statusToLabelKey[option] | transloco }}</span>
              @if (option === state.currentStatus) {
                <dojo-icon [icon]="'check'" [size]="16" />
              }
            </div>
          </button>
        }
      </div>
    </mat-menu>
  `,
  styleUrl: './challenge-activity-question-status-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengeActivityQuestionStatusSelectorComponent {
  constructor(
    private readonly challengeActivityQuestionAttemptQuery: ChallengeActivityQuestionAttemptQuery,
    private readonly challengeActivityQuestionAttemptService: ChallengeActivityQuestionAttemptService,
    private readonly destroyRef: DestroyRef,
  ) {}

  readonly isStatusSelectorOpened = signal(false);

  readonly statusOptions = CHALLENGE_ACTIVITY_QUESTION_ATTEMPT_STATUSES;

  readonly statusToLabelKey: Record<ChallengeActivityQuestionAttemptStatus, string> = {
    TODO: 'challenge_activity_question_status.todo',
    IN_PROGRESS: 'challenge_activity_question_status.in_progress',
    DONE: 'challenge_activity_question_status.done',
  };

  readonly state = connectState({
    currentStatus: this.challengeActivityQuestionAttemptQuery.active$.pipe(
      map((attempt: ChallengeActivityQuestionAttemptStoreModel | null) => attempt?.status ?? 'TODO'),
    ),
  });

  cycleStatus(status: ChallengeActivityQuestionAttemptStatus) {
    const currentIndex = this.statusOptions.indexOf(status);
    const nextIndex = (currentIndex + 1) % this.statusOptions.length;
    this.updateChallengeActivityQuestionAttemptStatus(this.statusOptions[nextIndex]);
  }

  updateChallengeActivityQuestionAttemptStatus(status: ChallengeActivityQuestionAttemptStatus) {
    this.challengeActivityQuestionAttemptService
      .updateQuestionAttemptStatus(status)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
